<script setup>
import dayjs from 'dayjs';
import Cookies from 'universal-cookie';
import { v4 as uuidv4 } from 'uuid';
import { computed, onMounted, watch } from 'vue';
import { ModalsContainer, useModal } from 'vue-final-modal';
import { useRoute } from 'vue-router';
import { useAuthStore } from '~/auth/stores/auth.store';
import HawkZipDownloadTrigger from '~/common/components/organisms/hawk-zip-download/hawk-zip-download-trigger.vue';
import AuthLayout from '~/common/layouts/auth.layout.vue';
import DefaultLayout from '~/common/layouts/default.layout.vue';
import PublicLayout from '~/common/layouts/public.layout.vue';
import Maintenance from '~/common/pages/maintenance.vue';

import { useCommonStore } from '~/common/stores/common.store';
import { accessTokenCookie, currentOrganizationCookie, purgeCommonStore } from '~/common/utils/common.utils';
import { getCookie, getHostName } from '~/common/utils/common.utils.js';

import { loadAtlas, loadMixpanel, startAtlas } from '~/common/utils/load-script.util.js';
import NaavixBubble from '~/naavix/naavix-bubble.vue';
import DetectTimezoneChange from '~/profile-settings/components/detect-timezone-change.vue';
import TaskDetailsPopupNew from '~/tasks/pages/task-details-new/task-details-popup.vue';

const route = useRoute();
const common_store = useCommonStore();
const auth_store = useAuthStore();
const is_loading_initial_data = ref(true);
const cookies = new Cookies();
const layout = computed(() => {
  if (auth_store.is_maintenance_enabled())
    return Maintenance;
  else if (route?.meta?.layout === 'public')
    return PublicLayout;
  else if (route?.meta?.layout === 'auth' || !auth_store.access_token)
    return AuthLayout;
  else
    return DefaultLayout;
});

// Can be removed if we call this when clicking in/out of a asset,
// which technically should result in the more or less same memory consumption.
watch(() => common_store.active_asset, () => {
  common_store.add_custom_data_to_marker_io();
});

watch(() => route?.name, setHead, { immediate: true });

auth_store.load_split();

function setHead() {
  const title_prefix = common_store?.active_asset
    ? common_store?.active_asset?.name
    : (auth_store?.current_organization?.name || 'TaskMapper');
  let title = title_prefix;
  if (route?.meta?.title)
    title += ` - ${route.meta.title}`;
  document.title = title;
}

async function initialize() {
  try {
    is_loading_initial_data.value = true;

    if (getHostName() !== 'localhost') {
      loadAtlas();
      auth_store.setup_idle_tracker();
      if (!sessionStorage.getItem('mixpanel_session_id'))
        sessionStorage.setItem('mixpanel_session_id', uuidv4());
      loadMixpanel();
    }

    if (!accessTokenCookie()) {
      is_loading_initial_data.value = false;
      return;
    }

    if (route?.query?.organization) {
      purgeCommonStore();
      currentOrganizationCookie('set', route.query.organization);
    }

    if (getHostName() !== 'localhost') {
      let disable_session_recording = false;
      if (auth_store?.logged_in_user_details?.user_role?.uid || auth_store?.logged_in_user_details?.email?.endsWith('@sensehawk.com') || auth_store?.logged_in_user_details?.email?.endsWith('@taskmapper.com'))
        disable_session_recording = true;

      startAtlas(
        ...(disable_session_recording
          ? [{
              recording: {
                enabled: false,
              },
            }]
          : []),
      );
      common_store.chat_widget_user_identification();
    }

    if (window.Marker) {
      window.Marker.on('capture', () => {
        common_store.add_custom_data_to_marker_io();
      });
    }
  }
  catch (e) {
    logger.error(e);
  }
  is_loading_initial_data.value = false;
}

// Watcher for changes in user's timezone setting
watch(() => auth_store?.logged_in_user_details?.timezone, () => {
  const { open: openTimezonePopup, close: closeTimezonePopup } = useModal({
    component: DetectTimezoneChange,
    attrs: {
      onClose() {
        closeTimezonePopup();
      },
    },
  });
  const guessed_tz = dayjs.tz.guess();
  const deferred_tz_date = getCookie('defer_tz_change') || 0;
  const user_tz = auth_store?.logged_in_user_details?.timezone;
  if (auth_store?.logged_in_user_details?.user_id && (user_tz !== guessed_tz) && (deferred_tz_date < dayjs().valueOf()))
    openTimezonePopup();
}, { immediate: true });

onMounted(() => {
  initialize();
  const $vueform = inject('$vueform');
  $vueform.value.i18n.locale = cookies.get('lang') || 'en';
});
</script>

<template>
  <component :is="layout" />
  <ModalsContainer />
  <template v-if="!is_loading_initial_data && auth_store?.logged_in_user_details?.user_id">
    <NaavixBubble />
    <HawkZipDownloadTrigger />
    <TaskDetailsPopupNew
      v-if="route?.query?.task || route?.query?.template"
      :key="route?.query?.task || route?.query?.template"
    />

    <FormDetailPopup v-if="route?.query?.form || route?.query?.form_id" :key="route?.query?.form" />
  </template>
</template>
